import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authService } from '@/services/auth-service';
import { useAuthStore } from '@/stores/auth-store';
import { consumePostLoginRedirect } from '@/services/post-login-redirect';

export default function AuthCallbackPage() {
  // We only support Microsoft (Entra) in this app; callback path is /auth/callback
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const login = useAuthStore((s) => s.login);
  const isRuntimeProd = (import.meta.env.VITE_ENVIRONMENT || '').toLowerCase() === 'prod';
  const handledRef = useRef(false);
  const errorTimerRef = useRef<number | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Prevent duplicate executions (React StrictMode or accidental re-renders)
      if (handledRef.current) return;
      handledRef.current = true;
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (!code) {
          setError('Missing authorization code');
          setLoading(false);
          return;
        }

        const attemptedKey = `oauth_callback_attempted:${code}`;
        const alreadyAttempted = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(attemptedKey) === '1';

        if (alreadyAttempted) {
          const recovered = await useAuthStore.getState().refreshAccessToken();
          if (recovered === 'success') {
            const { user } = useAuthStore.getState();
            if (user?.onboardingCompleted) {
              const redirectPath = consumePostLoginRedirect() || '/app';
              navigate(redirectPath, { replace: true });
            }
            else navigate('/onboarding', { replace: true });
            return;
          }
        }

        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem(attemptedKey, '1');
        }

        // Only Microsoft is supported; call backend callback for microsoft
        const result = await authService.handleOAuthCallback('microsoft', code);

        // Refresh token is managed via secure HttpOnly cookie in production.
        if (!isRuntimeProd && result.refreshToken) {
          localStorage.setItem('refreshToken', result.refreshToken);
        }
        // Persist id_token in sessionStorage for logout hint (less persistent than localStorage)
        if ((result as any).idToken) {
          try {
            sessionStorage.setItem('idToken', (result as any).idToken);
            localStorage.removeItem('idToken');
          } catch {
            // ignore storage errors
          }
        }
        // Ensure VMA is always set to production mode on any login (OAuth or native)
        try {
          localStorage.setItem('vma-use-test-data', 'false');
        } catch {
          // ignore storage errors
        }

        login(result.user as unknown as import('@/stores/auth-store').User, result.accessToken, result.expiresIn);

        // Redirect to app or onboarding
        if (result.user.onboardingCompleted) {
          const redirectPath = consumePostLoginRedirect() || '/app';
          navigate(redirectPath, { replace: true });
        }
        else navigate('/onboarding', { replace: true });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const lower = msg.toLowerCase();
        const isCodeReuseOrExpiry =
          lower.includes('authorization code')
          || lower.includes('invalid_grant')
          || lower.includes('aadsts70008')
          || lower.includes('gått ut')
          || lower.includes('already been used');

        if (isCodeReuseOrExpiry) {
          const recovered = await useAuthStore.getState().refreshAccessToken();
          if (recovered === 'success') {
            const { user } = useAuthStore.getState();
            if (user?.onboardingCompleted) {
              const redirectPath = consumePostLoginRedirect() || '/app';
              navigate(redirectPath, { replace: true });
            }
            else navigate('/onboarding', { replace: true });
            return;
          }
        }

        // If the error is an already-redeemed authorization code it's often
        // transient when the provider redirects twice. Delay showing it so a
        // subsequent successful callback can complete and navigate away.
        if (isCodeReuseOrExpiry) {
          // Show after short delay unless component unmounts (successful flow)
          errorTimerRef.current = window.setTimeout(() => {
            if (mounted) setError(msg);
          }, 1500) as unknown as number;
        } else {
          setError(msg);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      mounted = false;
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
    };
  }, [login, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="max-w-lg text-center">
        {error ? (
          <div>
            <div className="rounded-md bg-destructive/10 p-4 text-destructive">{error}</div>
            {error.toLowerCase().includes('authorization code') && (
              <div className="mt-4">
                <button
                  className="rounded bg-primary px-4 py-2 text-white"
                  onClick={() => {
                    // Retry by sending the user back to the start so they get a fresh code
                    window.location.assign('/');
                  }}
                >
                  Försök igen
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="rounded-md bg-muted p-4">Inloggning lyckades. Omdirigerar…</div>
        )}
      </div>
    </div>
  );
}
